import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { Plus } from 'lucide-react';
import { JobCard } from './JobCard';
import { T, stageMeta } from '../constants';

/** One pipeline stage: header with icon, name and count, then its cards. */
export const JobColumn = ({ stageId, jobs, onOpenJob, onAddToStage }) => {
  const meta = stageMeta(stageId);
  const Icon = meta.icon;
  const { setNodeRef, isOver } = useDroppable({ id: stageId });

  return (
    <section
      ref={setNodeRef}
      aria-label={`${meta.label}, ${jobs.length} ${jobs.length === 1 ? 'job' : 'jobs'}`}
      className="jt-column"
      style={{
        background: isOver ? `${meta.color}0A` : T.canvas,
        border: `1px solid ${isOver ? `${meta.color}55` : T.line}`,
        borderRadius: T.radiusLg,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 220,
        transition: 'background 0.18s, border-color 0.18s',
        fontFamily: T.font,
      }}
    >
      {/* Header */}
      <header style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '12px 13px 10px',
        borderBottom: `1px solid ${T.line}`,
      }}>
        <div style={{
          width: 26, height: 26, borderRadius: 8, flexShrink: 0,
          background: meta.bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon size={14} strokeWidth={2.1} color={meta.color} />
        </div>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: T.ink, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {meta.label}
        </span>
        <span style={{
          fontSize: 11, fontWeight: 800, color: meta.color,
          background: meta.bg, borderRadius: 20, padding: '1px 8px', minWidth: 22, textAlign: 'center',
        }}>
          {jobs.length}
        </span>
        <button
          type="button"
          onClick={() => onAddToStage(stageId)}
          title={`Add a job to ${meta.label}`}
          aria-label={`Add a job to ${meta.label}`}
          style={{
            border: 'none', background: 'transparent', color: T.faint,
            cursor: 'pointer', padding: 2, borderRadius: 6, display: 'flex', lineHeight: 0,
            transition: 'color 0.15s, background 0.15s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = T.brand; e.currentTarget.style.background = T.brandSoft; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = T.faint; e.currentTarget.style.background = 'transparent'; }}
        >
          <Plus size={14} strokeWidth={2.4} />
        </button>
      </header>

      {/* Cards */}
      <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 9, flex: 1 }}>
        {jobs.length === 0 ? (
          <button
            type="button"
            onClick={() => onAddToStage(stageId)}
            style={{
              flex: 1, minHeight: 96, width: '100%',
              border: `1.5px dashed ${T.line}`, borderRadius: T.radius,
              background: 'transparent', cursor: 'pointer', fontFamily: T.font,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4,
              color: T.faint, transition: 'all 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = T.brandBorder; e.currentTarget.style.color = T.brand; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = T.line; e.currentTarget.style.color = T.faint; }}
          >
            <Plus size={15} strokeWidth={2.2} />
            <span style={{ fontSize: 11.5, fontWeight: 600 }}>No jobs yet</span>
          </button>
        ) : (
          jobs.map(job => <JobCard key={job._id} job={job} onOpen={onOpenJob} />)
        )}
      </div>
    </section>
  );
};
